<template>
  <BaseDialog
    contentClass="invitation"
    ref="dialog"
    sidePanel
    :show-actions="true"
    :persistent="isDirty"
    @closeModal="closeDialog"
  >
    <template slot="title">{{ $t('page.admin-invites.new-invite') }}</template>
    <template slot="content">
      <form @submit.prevent="onSubmit" novalidate class="new-invitation__form">
        <div class="login-form__section-small">
          <FormInput
            :error="emailError || domainError"
            :label="$t('login.id')"
            has-gray-background
            name="email"
            type="email"
            ref="email"
            v-model="email"
            v-validate="'required|email'"
            @input="onEmailChange"
          />
          <p v-if="isEmailDomainError && !hasEmailError" class="new-invitation__domain-hint">
            {{ $t('page.admin-invites.email-domain-contact-hint') }}
            <button type="button" class="new-invitation__domain-hint-link" @click="openBeacon">
              {{ $t('page.keywords.contact') }}
            </button>
          </p>
        </div>

        <div v-if="roleOptions.length" class="login-form__section-small">
          <BaseSelect
            name="role"
            :label="$t('page.admin-invites.role')"
            :value="role"
            :items="roleOptions"
            item-text="text"
            item-value="value"
            @change="onRoleChange"
          />
          <p v-if="roleError" class="new-invitation__role-error">{{ roleError }}</p>
        </div>

        <div class="login-form__section-small">
          <LocaleProvider ref="localeProvider">
            <template slot-scope="{ currentLocale, locales }">
              <BaseSelect
                name="locale"
                :label="$t('page.admin-user-profile.language')"
                :value="currentLocale"
                :items="locales"
                item-text="name"
                item-value="locale"
                :hint="$t('page.admin-user-profile.language-hint')"
                @change="onLocaleChange"
              />
            </template>
          </LocaleProvider>
        </div>
        <div class="actions">
          <FormButton
            is-large
            :disabled="isFormInvalid"
            :is-outlined="isFormInvalid"
            type="submit"
            class="new-invitation__submit"
          >
            {{ $t('page.admin-invites.send-invite') }}
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import FormButton from '@/components/form/FormButton';
import BaseDialog from '@/components/base/BaseDialog';
import FormInput from '@/components/form/FormInput.vue';
import LocaleProvider from '@/components/locale/LocaleProvider';
import BaseSelect from '@/components/base/BaseSelect';
import { isAdminTenant, invitableRoles, ROLE } from '@/models/role';
import { Beacon } from '@/plugins/beacon';

import { mapActions, mapGetters } from 'vuex';

const ALOHA_DOMAIN = 'aloha.app';

function domainFromEmail(email) {
  if (!email || typeof email !== 'string') return '';

  return email.trim().toLowerCase().split('@').pop() || '';
}

function isAlohaDomain(domain) {
  return domain === ALOHA_DOMAIN || domain.endsWith('.' + ALOHA_DOMAIN);
}

export default {
  name: 'AddInviteDialog',
  components: {
    FormButton,
    BaseDialog,
    LocaleProvider,
    BaseSelect,
    FormInput,
  },
  data() {
    return {
      isDirty: false,
      email: '',
      locale: 'fr-CA',
      role: ROLE.USER,
      roleError: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    roleOptions() {
      const role = this.currentUser && this.currentUser.role;
      return invitableRoles(role).map((r) => ({ text: this.$t(`roles.${r}`), value: r }));
    },
    ...mapGetters('tenant', ['users']),
    ...mapGetters('invites', ['invites']),
    /** No invitation has been sent yet — likely the customer's first admin being onboarded. */
    isFirstMember() {
      return (this.invites || []).length === 0;
    },
    /** Default the first member to manager (the customer's admin); everyone else to user. */
    defaultRole() {
      if (this.isFirstMember && this.roleOptions.some((option) => option.value === ROLE.MANAGER)) {
        return ROLE.MANAGER;
      }
      return ROLE.USER;
    },
    isFormInvalid() {
      return !!!this.email || this.emailError !== undefined || this.domainError !== undefined;
    },
    emailError() {
      const error = this.errors.items.find((x) => x.field === 'email');
      if (error) return error.msg;
      return undefined;
    },
    hasEmailError() {
      return !!this.errors.items.find((x) => x.field === 'email');
    },
    domainError() {
      // Domain check: warn before submit (same UX as email format validation)
      if (!this.canBypassDomainCheck && this.allowedDomains.length > 0 && this.email) {
        const domain = domainFromEmail(this.email);
        if (domain && !this.allowedDomains.includes(domain)) {
          return this.$t('page.admin-invites.email-domain-not-allowed');
        }
      }
      return undefined;
    },
    /** Domains allowed for invites (from tenant users + invites), excluding aloha.app */
    allowedDomains() {
      const domains = new Set();
      const add = (email) => {
        const d = domainFromEmail(email);
        if (d && !isAlohaDomain(d)) domains.add(d);
      };
      (this.users || []).forEach((u) => add(u.email));
      (this.invites || []).forEach((inv) => add(inv.email));
      return Array.from(domains);
    },
    canBypassDomainCheck() {
      return this.currentUser && this.currentUser.role && isAdminTenant(this.currentUser.role);
    },
    /** Domain hint block: only when domain is wrong, not when format/required fails first. */
    isEmailDomainError() {
      return (
        !this.canBypassDomainCheck &&
        this.allowedDomains.length > 0 &&
        this.email &&
        domainFromEmail(this.email) &&
        !this.allowedDomains.includes(domainFromEmail(this.email))
      );
    },
  },
  methods: {
    ...mapActions('invites', ['sendInvite']),
    ...mapActions('tenant', ['getTenant']),
    async openDialog() {
      this.email = '';
      this.role = ROLE.USER;
      this.roleError = '';
      this.$refs.dialog.openModal();

      await this.getTenant(this.currentUser.tenantId);

      this.role = this.defaultRole;

      this.$nextTick(() => {
        const refInput = this.$refs.email && this.$refs.email.$el && this.$refs.email.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.$validator.reset();
      this.errors.clear();
      this.isDirty = false;
      this.$refs.dialog.isModalOpen = false;
    },
    onLocaleChange(locale) {
      this.isDirty = true;
      this.locale = locale;
    },
    onRoleChange(role) {
      this.isDirty = true;
      this.role = role;
      this.roleError = '';
    },
    onEmailChange() {
      this.isDirty = true;
    },
    openBeacon() {
      this.$track.contactUs('beacon');
      Beacon.toggle();
      Beacon.navigate('ask');
    },
    async onSubmit() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.roleError = '';

      const email = this.email
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

      if (!this.canBypassDomainCheck && this.allowedDomains.length > 0) {
        const domain = domainFromEmail(email);
        if (domain && !this.allowedDomains.includes(domain)) {
          return;
        }
      }

      try {
        await this.sendInvite({ tenantId: this.currentUser.tenantId, email, locale: this.locale, role: this.role });
        this.$toast.success(this.$t('page.admin-invites.new-invite-created'));
        this.$emit('actionSucceeded');
        this.closeDialog();
      } catch (error) {
        // Server is the source of truth: if it rejects the requested role, keep the
        // dialog open and surface an inline error rather than silently closing.
        if (error && error.status === 403) {
          this.roleError = this.$t('page.admin-invites.role-forbidden');
        } else {
          this.$toast.error(error && error.data ? error.data : error);
          this.closeDialog();
        }
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__invitation {
  .form-input__error-msg {
    position: relative;
    top: 0;
  }

  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .actions {
    display: flex;
    justify-content: center;
    padding: 2rem;
  }
}

.new-invitation__role-error {
  margin: 4px 0 0;
  font-size: 13px;
  color: $color-error;
}

.new-invitation__domain-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: $color-normal;

  &-link {
    background: none;
    border: none;
    padding: 0;
    font-size: inherit;
    color: $color-accent;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: darken($color-accent, 10%);
    }
  }
}
</style>
